import { useEffect, useState } from 'react';
import config from '../config';
import { getMyCredits } from '../services/CreditsService';

/**
 * The person's credits (services/CreditsService.js, GET /api/v1/me/credits), shared by every place that shows them: the
 * chat's notice and the Change plan dialog. Read once per page and again at most once a minute; spending reaches the
 * gateway up to an hour late, so it is also asked again every five minutes while the page is visible. Without
 * `config.sandboxFirst`, or where the gateway has no billing (an older one) or fails, there is nothing: `me` is null
 * and nothing about credits shows, as before.
 */
export const CREDITS_EVENT = 'flow:credits-changed';
const STALE_MS = 60000;
const REFRESH_MS = 5 * 60000;

let me = null;
let settled = false;
let loading = null;
let askedAt = 0;

const notify = () => window.dispatchEvent(new CustomEvent(CREDITS_EVENT));

/** Asks the gateway (shared by every caller; at most once a minute unless `force`). */
export function loadCredits({ force = false } = {}) {
  if (!config.sandboxFirst) { return Promise.resolve(null); }
  if (force || !loading || Date.now() - askedAt > STALE_MS) {
    askedAt = Date.now();
    loading = getMyCredits()
      .then((next) => { me = next; })
      // Unknown: nothing about credits shows. A later read tries again.
      .catch(() => {})
      .finally(() => { settled = true; notify(); });
  }
  return loading.then(() => me);
}

/** Forgets what was loaded (tests). */
export function resetCredits() {
  me = null;
  settled = false;
  loading = null;
  askedAt = 0;
}

const read = () => ({ ready: settled, me });

/** `{ ready, me, refresh }`: the person's credits, kept in sync across components. */
export default function useCredits() {
  const [state, setState] = useState(read);
  useEffect(() => {
    const sync = () => setState(read());
    window.addEventListener(CREDITS_EVENT, sync);
    loadCredits();
    sync();
    const timer = setInterval(() => { if (document.visibilityState === 'visible') { loadCredits({ force: true }); } }, REFRESH_MS);
    return () => { window.removeEventListener(CREDITS_EVENT, sync); clearInterval(timer); };
  }, []);
  return { ...state, refresh: () => loadCredits({ force: true }) };
}
